import {
  Box,
  LoadingOverlay,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { set } from 'lodash/fp';
import React, { useEffect } from 'react';

import { useStoreSettings } from '@portals/api/partners';
import { PeriodEnum, ProductPricingModel } from '@portals/types';

import { PaymentSettingPrices } from './PaymentSettingPrices';
import { PaymentSettingItemType } from '../../../types/store-listings.types';
import { useStoreListingWizard } from '../hooks';

export function UsageBasedPaymentSettings() {
  const storeSettings = useStoreSettings();
  const { contextData, setContextData } = useStoreListingWizard();

  useEffect(() => {
    // No need to initialize if there are prices set already
    if (contextData.paymentSettings.usage_based) return;

    // Initialize "empty" prices for usage based pricing model
    const prices: PaymentSettingItemType['prices'] = (
      storeSettings.data?.currencies ?? []
    ).map((currency) => ({
      currency,
      amountInScu: null,
      error: false,
    }));

    setContextData((prev) =>
      set(
        ['paymentSettings', ProductPricingModel.UsageBased],
        {
          prices,
          period: ProductPricingModel.UsageBased,
        } satisfies PaymentSettingItemType,
        prev
      )
    );
  }, [
    contextData.paymentSettings.usage_based,
    setContextData,
    storeSettings.data?.currencies,
  ]);

  if (storeSettings.isLoading || !storeSettings.data) {
    return <LoadingOverlay visible />;
  }

  return (
    <Box maw={400}>
      <Text size="md" color="gray.8" mb="xxl">
        Usage based pricing model
      </Text>

      <Stack spacing="xl">
        <SegmentedControl
          readOnly={contextData.storeListing?.editable === false}
          data={[
            {
              value: 'payAsYouGo',
              label: 'Pay-as-you-go',
            },
            {
              value: 'package',
              label: 'Package (coming soon)',
              disabled: true,
            },
          ]}
        />

        <Select
          label="Billing period"
          value={PeriodEnum.Monthly}
          readOnly={contextData.storeListing?.editable === false}
          data={[
            { value: PeriodEnum.Monthly, label: 'Monthly' },
            {
              value: PeriodEnum.Yearly,
              label: 'Yearly (coming soon)',
              disabled: true,
            },
          ]}
        />

        <Stack>
          <Text size="sm" color="gray.6">
            Price per{' '}
            <Text span c="blue_gray.9" weight={700}>
              {contextData.selectedProduct?.usage_based_unit_name}
            </Text>
          </Text>

          <PaymentSettingPrices
            period={ProductPricingModel.UsageBased}
            defaultCurrency={storeSettings.data.default_currency}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
